/**
 * @file `DeliveryTimeline` — a per-email delivery event sequence (sent →
 *       delivered → clicked / bounced / complained) rendered as a vertical
 *       timeline. Used by the campaign-detail recipient pane and the History
 *       per-email detail. Click is the kept engagement signal (#1690 Part 4;
 *       open tracking is dropped platform-wide — there is no `opened` node).
 * @module @labcraft/admin/ui/emails/delivery-timeline
 *
 * The `sent` node anchors on `sentEventAt ?? createdAt` — the provider's
 * `email.sent` event when present, else the row's submitted-at, so there is
 * always a timestamped first node (#1606 Part 6). Later nodes appear only
 * for events that actually fired. A bounce / failure carries its error text.
 */

import type { JSX } from 'react'
import type { CampaignRecipient } from '../../shared/emails_routes.ts'

/** Timeline node tone → dot colour. */
type TlTone = 'sent' | 'ok' | 'warn' | 'bad'

/** One delivery node. */
type TlItem = {
  state: string
  at: string
  tone: TlTone
  err?: string
}

/** `2026-05-21T12:00:00Z` → `May 21, 12:00`; invalid → em dash. */
function formatStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Build the ordered delivery nodes for one recipient. */
function timelineFor(r: CampaignRecipient): TlItem[] {
  const items: TlItem[] = []
  const sentAt = r.sentEventAt ?? r.createdAt
  if (sentAt) items.push({ state: 'sent', at: sentAt, tone: 'sent' })
  if (r.delayedAt)
    items.push({ state: 'delayed', at: r.delayedAt, tone: 'warn' })
  if (r.deliveredAt) {
    items.push({ state: 'delivered', at: r.deliveredAt, tone: 'ok' })
  }
  if (r.clickedAt) {
    const n = r.clickCount ?? 0
    items.push({
      state: n > 1 ? `clicked (${n})` : 'clicked',
      at: r.clickedAt,
      tone: 'ok',
    })
  }
  if (r.bouncedAt) {
    items.push({
      state: 'bounced',
      at: r.bouncedAt,
      tone: 'bad',
      err: r.error ?? undefined,
    })
  }
  if (r.complainedAt) {
    items.push({ state: 'complained', at: r.complainedAt, tone: 'bad' })
  }
  if (r.sendStatus === 'failed' && sentAt) {
    items.push({
      state: 'failed',
      at: sentAt,
      tone: 'bad',
      err: r.error ?? undefined,
    })
  }
  return items
}

/** Render the delivery timeline for one recipient. */
export function DeliveryTimeline({
  recipient,
}: {
  recipient: CampaignRecipient
}): JSX.Element {
  const items = timelineFor(recipient)
  if (items.length === 0) {
    return <div className="tl-empty">No delivery events recorded yet.</div>
  }
  return (
    <div className="timeline">
      {items.map((it, i) => (
        <div className="tl-item" key={`${it.state}-${i}`}>
          <div className="tl-item__rail">
            <span className={`tl-dot tl-dot--${it.tone}`} />
            <span className="tl-line" />
          </div>
          <div className="tl-body">
            <div className="tl-body__state">{it.state}</div>
            <div className="tl-body__at">{formatStamp(it.at)}</div>
            {it.err !== undefined && (
              <div className="tl-body__err">{it.err}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
